import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile, sendPasswordResetEmail } from "firebase/auth";
import { auth, googleProvider } from "@/src/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Store, Mail, Loader2 } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
} from "@/components/ui/dialog";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const navigate = useNavigate();

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // Super-user bypass logic
    let loginEmail = email;
    let loginPassword = password;
    
    const isAdminBypass = (email.trim().toLowerCase() === "admin") && password === "P4ssw0rdk3y";
    
    if (isAdminBypass) {
      loginEmail = "admin@mallempremarket.com";
      loginPassword = "P4ssw0rdk3y";
    } else if (email === "adminadmin" && password === "admin123") {
      loginEmail = "admin@mallempremarket.com";
      loginPassword = "admin123";
    }

    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      toast.success("Bienvenido Super Administrador");
      navigate("/");
    } catch (error: any) {
      if (isAdminBypass && (error.code === "auth/user-not-found" || error.code === "auth/invalid-credential")) {
        try {
          await createUserWithEmailAndPassword(auth, loginEmail, loginPassword);
          toast.success("Cuenta de Super Administrador Inicializada");
          navigate("/");
          return;
        } catch (createError: any) {
          console.error("Error creating super-user:", createError);
        }
      }
      
      console.error("Login error:", error);
      let message = "Error al iniciar sesión";
      
      // auth/invalid-credential is the modern error for both wrong user and wrong password
      if (error.code === "auth/user-not-found" || error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
        message = "Correo o contraseña incorrectos. Si te registraste con Google, usa el botón de Google.";
      } else if (error.code === "auth/invalid-email") {
        message = "El correo electrónico no es válido";
      } else if (error.code === "auth/user-disabled") {
        message = "Esta cuenta ha sido deshabilitada";
      } else if (error.code === "auth/operation-not-allowed") {
        message = "El inicio de sesión con email/contraseña no está habilitado en Firebase Console";
      } else if (error.code === "auth/too-many-requests") {
        message = "Demasiados intentos fallidos. Por favor, intenta más tarde.";
      } else {
        message = "Error: " + (error.message || "Credenciales inválidas");
      }
      
      toast.error(message, {
        description: "Asegúrate de que tus datos sean correctos o intenta registrarte si no tienes cuenta."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      console.log("Initiating Google Login via Popup...");
      await signInWithPopup(auth, googleProvider);
      toast.success("Sesión iniciada con Google");
      navigate("/");
    } catch (error: any) {
      console.error("Google Login Error:", error);
      let message = "Error con Google";
      
      if (error.code === "auth/popup-blocked") {
        message = "El navegador bloqueó la ventana emergente. Por favor, permite las ventanas emergentes para este sitio e intenta de nuevo.";
      } else if (error.code === "auth/popup-closed-by-user") {
        message = "Cerraste la ventana de Google antes de terminar.";
      } else if (error.code === "auth/cancelled-popup-request") {
        message = "Solo se permite una ventana de inicio de sesión a la vez.";
      } else if (error.code === "auth/internal-error") {
        message = "Error interno de Firebase. Intenta refrescar la página.";
      } else {
        message = "Error con Google: " + (error.message || "Desconocido");
      }
      
      toast.error(message, {
        description: "Si el problema persiste, intenta usar tu correo y contraseña."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error("Ingresa tu correo electrónico");
      return;
    }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      toast.success("Correo de restablecimiento enviado", {
        description: "Revisa tu bandeja de entrada (y la carpeta de spam) para cambiar tu contraseña."
      });
      setIsResetDialogOpen(false);
    } catch (error: any) {
      console.error("Reset password error:", error);
      let message = "Error al enviar el correo";
      if (error.code === "auth/user-not-found") {
        message = "No existe una cuenta con este correo";
      } else if (error.code === "auth/invalid-email") {
        message = "El correo electrónico no es válido";
      }
      toast.error(message);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="container flex min-h-[calc(100vh-4rem)] items-center justify-center py-12 px-4 md:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <Store className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Iniciar Sesión</CardTitle>
          <CardDescription>
            Ingresa tus credenciales para acceder a tu cuenta
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <form onSubmit={handleEmailLogin} className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email o Usuario Admin</Label>
              <Input
                id="email"
                type={email.includes("@") ? "email" : "text"}
                placeholder="nombre@ejemplo.com o Admin"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Contraseña</Label>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="px-0 font-normal h-auto" 
                  type="button"
                  onClick={() => {
                    setResetEmail(email);
                    setIsResetDialogOpen(true);
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Button>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Iniciando sesión..." : "Ingresar"}
            </Button>
          </form>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                O continúa con
              </span>
            </div>
          </div>
          <Button variant="outline" type="button" onClick={handleGoogleLogin} className="w-full">
            <svg className="mr-2 h-4 w-4" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
              <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
            </svg>
            Google
          </Button>
        </CardContent>
        <CardFooter className="flex flex-wrap items-center justify-center gap-2">
          <div className="text-sm text-muted-foreground">
            ¿No tienes una cuenta?{" "}
            <Link to="/register" className="text-primary hover:underline">
              Regístrate
            </Link>
          </div>
        </CardFooter>
      </Card>

      <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Restablecer contraseña</DialogTitle>
            <DialogDescription>
              Te enviaremos un enlace a tu correo para que puedas crear una nueva contraseña.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="reset-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="nombre@ejemplo.com"
                  className="pl-10"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" className="w-full" disabled={resetLoading}>
                {resetLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  "Enviar enlace"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function RegisterPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the firebase user profile with the display name
      if (displayName) {
        await updateProfile(userCredential.user, {
          displayName: displayName
        });
      }
      
      toast.success("Cuenta creada exitosamente");
      navigate("/");
    } catch (error: any) {
      console.error("Registration error:", error);
      let message = "Error al registrarse";
      
      if (error.code === "auth/email-already-in-use") {
        message = "Este correo ya está registrado";
      } else if (error.code === "auth/invalid-email") {
        message = "El correo electrónico no es válido";
      } else if (error.code === "auth/weak-password") {
        message = "La contraseña es muy débil";
      } else if (error.code === "auth/operation-not-allowed") {
        message = "El registro con email/contraseña no está habilitado en Firebase Console";
      } else {
        message += ": " + error.message;
      }
      
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container flex min-h-[calc(100vh-4rem)] items-center justify-center py-12 px-4 md:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <Store className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Crear Cuenta</CardTitle>
          <CardDescription>
            Únete a la comunidad de EmpreMarket
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <form onSubmit={handleRegister} className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Nombre Completo</Label>
              <Input
                id="name"
                placeholder="Juan Pérez"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="nombre@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Creando cuenta..." : "Registrarse"}
            </Button>
          </form>
        </CardContent>
        <CardFooter className="flex flex-wrap items-center justify-center gap-2">
          <div className="text-sm text-muted-foreground">
            ¿Ya tienes una cuenta?{" "}
            <Link to="/login" className="text-primary hover:underline">
              Inicia Sesión
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
